/**
 * O envio de um lote de arquivos pelo recebimento (E133.F2, GUS-106, ADR-0089).
 *
 * Cada lote abre um recebimento, e cada arquivo vira uma linha com o estado dele. A linha que
 * falha guarda o arquivo e o recebimento: "Tentar novamente" manda os mesmos bytes ao mesmo
 * recebimento, e o servidor não os grava duas vezes. Se o recebimento não abriu ou sumiu, a
 * retentativa abre outro. O arquivo acima de 50 MB não é enviado; a linha diz o limite.
 *
 * E135.F2 (GUS-158, ADR-0091): quando nenhuma linha de um recebimento está em envio ou com falha,
 * e alguma entrou, o recebimento é concluído e o manifesto da plataforma é selado. Depois disso
 * ele não aceita arquivo (409): a retentativa abre outro, que é o complemento.
 *
 * E135.F2.C01 (F-E135.F2-01): o recibo só está pronto quando o servidor devolve o manifesto. A
 * conclusão em curso e a que falhou contam nas pendências, e a que falhou fica em
 * `recibosNaoSelados`, com `concluirDeNovo`: a tela diz que o recibo não foi fechado em vez de
 * seguir como se estivesse.
 *
 * E135.F2.C02 (F-E135.F2-02): a falha também sobrevive a recarregar a página. O chamador passa do
 * recibo gravado os recebimentos com arquivo e sem conclusão (`abertosNoRecibo`); o que nenhuma
 * linha desta tela acompanha é recibo não fechado, conta nas pendências e conclui pelo mesmo
 * `concluirDeNovo`.
 */
import { useCallback, useRef, useState } from 'react';
import type { AxiosInstance } from 'axios';

import {
  DocumentError,
  ehPacote,
  type DocumentErrorReason,
  type SourceDocumentKindEnviado,
} from '@/lib/case_source_documents';
import {
  MAX_BYTES_DO_ARQUIVO,
  abrirRecebimento,
  concluirRecebimento,
  enviarAoRecebimento,
  type EntradaNoRecebimentoData,
} from '@/lib/recebimentos';

export type EstadoDaLinha = 'enviando' | 'falhou' | 'concluido';

export interface LinhaDoEnvio {
  localId: string;
  fileName: string;
  percent: number;
  /** O pacote ZIP: depois dos 100% o servidor abre e lê cada arquivo dele. */
  pacote: boolean;
  /** O recebimento em que a linha está, depois de aberto. */
  recebimentoId?: string | undefined;
  estado: EstadoDaLinha;
  erro?: DocumentErrorReason | undefined;
  entrada?: EntradaNoRecebimentoData | undefined;
  /** A falha que se resolve tentando de novo (a acima de 50 MB não se resolve). */
  retentavel: boolean;
}

interface Pendente {
  file: File;
  recebimentoId: string | null;
}

/** Em que recebimento cada linha está e como ela terminou: o que decide a conclusão dele. */
interface EnvioDaLinha {
  recebimentoId: string;
  estado: EstadoDaLinha;
}

/** O recebimento perdido: a retentativa abre outro em vez de insistir nele. */
const RECEBIMENTO_PERDIDO: readonly DocumentErrorReason[] = [
  'recebimento_not_found',
  'recebimento_concluido',
];

export interface OpcoesDoEnvio {
  client: AxiosInstance;
  caseId: string;
  kind: SourceDocumentKindEnviado;
  /** Cada arquivo que entrou: o chamador relê a lista e guarda os não examinados. */
  onEntrada: (entrada: EntradaNoRecebimentoData) => void;
  /** E111.F1: tempo esgotado pode ter entrado — o chamador relê a lista do caso. */
  onTalvezRecebido?: () => void;
  /** E135.F2: o recebimento foi selado — o chamador relê o recibo. */
  onConcluido?: (recebimentoId: string) => void;
  /** E149.F2.C01 (F-E149.F2-01): as linhas de um lote novo, antes de qualquer envio — quem chama
   * guarda nelas o que o lote informou, para não ler depois o que outro lote informou. */
  onLote?: (localIds: readonly string[]) => void;
  /** E149.F2.C01: o recebimento aberto para estas linhas — o do lote, ou o novo de uma retentativa
   * cujo recebimento se perdeu. */
  onAberto?: (recebimentoId: string, localIds: readonly string[]) => void;
  /** E135.F2.C02: do recibo gravado, os recebimentos com arquivo e sem conclusão. */
  abertosNoRecibo?: readonly string[] | undefined;
  /** E177.F4 (ADR-0130 §2.2): o bloco da CCB declarada — cada lote abre o recebimento dela. */
  ccbId?: string | undefined;
}

export interface EnvioPorRecebimento {
  linhas: readonly LinhaDoEnvio[];
  enviarLote: (files: readonly File[]) => Promise<void>;
  tentarDeNovo: (localId: string) => Promise<void>;
  removerLinha: (localId: string) => void;
  /** Os recebimentos cuja conclusão falhou ou que o recibo gravado mostra abertos. */
  recibosNaoSelados: readonly string[];
  concluirDeNovo: (recebimentoId: string) => Promise<void>;
  /** O que segura a conferência: linha em envio ou com falha, e recibo selando ou não fechado. */
  pendencias: number;
}

/** A conclusão de um recebimento que ainda não devolveu o manifesto. */
type Selagem = 'selando' | 'falhou';

const SEM_ABERTOS: readonly string[] = [];

function razao(err: unknown): DocumentErrorReason {
  return err instanceof DocumentError ? err.reason : 'unknown';
}

function novaLinha(file: File): LinhaDoEnvio {
  const grande = file.size > MAX_BYTES_DO_ARQUIVO;
  return {
    localId: `local-${crypto.randomUUID()}`,
    fileName: file.name,
    percent: 0,
    pacote: ehPacote(file),
    estado: grande ? 'falhou' : 'enviando',
    ...(grande ? { erro: 'file_too_large' as const } : {}),
    retentavel: !grande,
  };
}

export function useEnvioPorRecebimento(opcoes: OpcoesDoEnvio): EnvioPorRecebimento {
  const { client, caseId, kind, onEntrada, onTalvezRecebido, onConcluido, onLote, onAberto } =
    opcoes;
  const ccbId = opcoes.ccbId;
  const abertosNoRecibo = opcoes.abertosNoRecibo ?? SEM_ABERTOS;
  const [linhas, setLinhas] = useState<readonly LinhaDoEnvio[]>([]);
  const [selagens, setSelagens] = useState<ReadonlyMap<string, Selagem>>(new Map());
  // Os selados nesta tela: o recibo relido antes da conclusão ainda os traz abertos.
  const [selados, setSelados] = useState<ReadonlySet<string>>(new Set());
  // O arquivo e o recebimento de cada linha, para a retentativa; não é estado de tela.
  const pendentes = useRef(new Map<string, Pendente>());
  const envios = useRef(new Map<string, EnvioDaLinha>());
  const concluidos = useRef(new Set<string>());

  const marcarSelagem = useCallback((recebimentoId: string, selagem: Selagem | null) => {
    setSelagens((atual) => {
      const proximo = new Map(atual);
      if (selagem === null) proximo.delete(recebimentoId);
      else proximo.set(recebimentoId, selagem);
      return proximo;
    });
  }, []);

  const selar = useCallback(
    async (recebimentoId: string) => {
      if (concluidos.current.has(recebimentoId)) return;
      concluidos.current.add(recebimentoId);
      marcarSelagem(recebimentoId, 'selando');
      try {
        await concluirRecebimento(client, caseId, recebimentoId);
        setSelados((atual) => new Set([...atual, recebimentoId]));
        marcarSelagem(recebimentoId, null);
        onConcluido?.(recebimentoId);
      } catch {
        // O recibo não foi fechado: a tela diz e oferece concluir de novo (F-E135.F2-01).
        concluidos.current.delete(recebimentoId);
        marcarSelagem(recebimentoId, 'falhou');
      }
    },
    [caseId, client, marcarSelagem, onConcluido],
  );

  const talvezConcluir = useCallback(
    async (recebimentoId: string) => {
      const dele = [...envios.current.values()].filter((e) => e.recebimentoId === recebimentoId);
      const aberto = dele.some((e) => e.estado !== 'concluido');
      if (!aberto && dele.length > 0) await selar(recebimentoId);
    },
    [selar],
  );

  const marcar = useCallback(
    (localId: string, recebimentoId: string, estado: EstadoDaLinha) => {
      const anterior = envios.current.get(localId)?.recebimentoId;
      envios.current.set(localId, { recebimentoId, estado });
      if (anterior !== undefined && anterior !== recebimentoId) void talvezConcluir(anterior);
      if (estado !== 'enviando') void talvezConcluir(recebimentoId);
    },
    [talvezConcluir],
  );

  const atualizar = useCallback((localId: string, mudanca: Partial<LinhaDoEnvio>) => {
    setLinhas((lista) => lista.map((l) => (l.localId === localId ? { ...l, ...mudanca } : l)));
  }, []);

  const falhar = useCallback(
    (localId: string, erro: DocumentErrorReason) => {
      atualizar(localId, { estado: 'falhou', erro, retentavel: true });
      if (erro === 'timeout') onTalvezRecebido?.();
    },
    [atualizar, onTalvezRecebido],
  );

  const enviarLinha = useCallback(
    async (localId: string, recebimentoId: string) => {
      const pendente = pendentes.current.get(localId);
      if (pendente === undefined) return;
      pendente.recebimentoId = recebimentoId;
      marcar(localId, recebimentoId, 'enviando');
      atualizar(localId, { estado: 'enviando', percent: 0, erro: undefined, recebimentoId });
      try {
        const entrada = await enviarAoRecebimento(client, caseId, recebimentoId, {
          file: pendente.file,
          kind,
          onProgress: (percent) => atualizar(localId, { percent }),
        });
        pendentes.current.delete(localId);
        atualizar(localId, { estado: 'concluido', entrada });
        onEntrada(entrada);
        marcar(localId, recebimentoId, 'concluido');
      } catch (err) {
        const motivo = razao(err);
        if (RECEBIMENTO_PERDIDO.includes(motivo)) pendente.recebimentoId = null;
        falhar(localId, motivo);
        marcar(localId, recebimentoId, 'falhou');
      }
    },
    [atualizar, caseId, client, falhar, kind, marcar, onEntrada],
  );

  const abrir = useCallback(
    async (localIds: readonly string[]): Promise<string | null> => {
      let recebimentoId: string;
      try {
        recebimentoId = (await abrirRecebimento(client, caseId, ccbId)).id;
      } catch (err) {
        for (const id of localIds) falhar(id, razao(err));
        return null;
      }
      onAberto?.(recebimentoId, localIds);
      return recebimentoId;
    },
    [caseId, ccbId, client, falhar, onAberto],
  );

  const enviarLote = useCallback(
    async (files: readonly File[]) => {
      if (files.length === 0) return;
      const novas = files.map((file) => ({ file, linha: novaLinha(file) }));
      for (const { file, linha } of novas) {
        pendentes.current.set(linha.localId, { file, recebimentoId: null });
      }
      setLinhas((lista) => [...lista, ...novas.map((n) => n.linha)]);
      onLote?.(novas.map((n) => n.linha.localId));
      const aEnviar = novas
        .filter((n) => n.linha.estado === 'enviando')
        .map((n) => n.linha.localId);
      if (aEnviar.length === 0) return;
      const recebimentoId = await abrir(aEnviar);
      if (recebimentoId === null) return;
      // O lote inteiro entra no recebimento antes do primeiro envio: ele só fecha no fim.
      for (const localId of aEnviar)
        envios.current.set(localId, { recebimentoId, estado: 'enviando' });
      setLinhas((lista) =>
        lista.map((l) => (aEnviar.includes(l.localId) ? { ...l, recebimentoId } : l)),
      );
      for (const localId of aEnviar) await enviarLinha(localId, recebimentoId);
    },
    [abrir, enviarLinha, onLote],
  );

  const tentarDeNovo = useCallback(
    async (localId: string) => {
      const pendente = pendentes.current.get(localId);
      if (pendente === undefined) return;
      const recebimentoId = pendente.recebimentoId ?? (await abrir([localId]));
      if (recebimentoId !== null) await enviarLinha(localId, recebimentoId);
    },
    [abrir, enviarLinha],
  );

  const removerLinha = useCallback(
    (localId: string) => {
      pendentes.current.delete(localId);
      const envio = envios.current.get(localId);
      envios.current.delete(localId);
      setLinhas((lista) => lista.filter((l) => l.localId !== localId));
      if (envio !== undefined) void talvezConcluir(envio.recebimentoId);
    },
    [talvezConcluir],
  );

  // Aberto no recibo gravado e sem linha nesta tela: a página foi recarregada (ou a linha removida)
  // com o recibo ainda aberto. A linha que está nele cuida da conclusão.
  const deixados = abertosNoRecibo.filter(
    (id) => !selados.has(id) && !selagens.has(id) && !linhas.some((l) => l.recebimentoId === id),
  );
  const falharam = [...selagens].filter(([, s]) => s === 'falhou').map(([id]) => id);
  const pendencias =
    linhas.filter((l) => l.estado !== 'concluido').length + selagens.size + deixados.length;
  return {
    linhas,
    enviarLote,
    tentarDeNovo,
    removerLinha,
    recibosNaoSelados: [...falharam, ...deixados],
    concluirDeNovo: selar,
    pendencias,
  };
}
